import { useState } from "react";
import { ChevronDown, Check, CalendarClock } from "lucide-react";
import { FadeUp, SectionTag } from "../components/site/Reveal";
import { ContactForm } from "../components/site/ContactForm";
import { CALENDLY_URL, FAQ, CONTACT_EMAIL } from "../content";

const PREP = [
  "Know your numbers: revenue, burn, raise target, or exit timeline",
  "Know your ask: capital, partnership, market entry, or exit",
  "Be ready to name what is blocking you, plainly",
  "Bring one decision you need to make in the next 90 days",
];

const FaqItem = ({ item, index }) => {
  const [open, setOpen] = useState(index === 0);
  return (
    <div data-testid={`faq-item-${index}`} className="border border-gold/25 bg-white">
      <button
        data-testid={`faq-toggle-${index}`}
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
      >
        <span className="font-serif text-lg font-medium text-ink">{item.q}</span>
        <ChevronDown
          size={18}
          className={`shrink-0 text-golddeep transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <div
        className={`grid transition-all duration-300 ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
      >
        <div className="overflow-hidden">
          <p className="px-6 pb-6 text-[15px] leading-relaxed text-slate-600">{item.a}</p>
        </div>
      </div>
    </div>
  );
};

export default function Book() {
  return (
    <main data-testid="book-page" className="pt-24">
      <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
        <div className="grid items-start gap-16 lg:grid-cols-2">
          <div>
            <FadeUp>
              <SectionTag>Conversations with Roy</SectionTag>
              <h1 className="mt-4 font-serif text-4xl font-bold tracking-tight text-ink sm:text-5xl">
                Twenty minutes.<br />
                <span className="gold-text italic">Zero fluff.</span>
              </h1>
              <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-600">
                This is a working conversation, not a sales call. You bring the situation:
                the raise, the deal, the competitor, the exit. You get a fast, honest read
                on whether it can be won, how, and what your first move is.
              </p>
            </FadeUp>
            <FadeUp delay={0.12}>
              <div className="mt-10 border border-gold/25 bg-white p-8">
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-golddeep">
                  Come prepared
                </p>
                <ul className="mt-5 space-y-3.5">
                  {PREP.map((p) => (
                    <li key={p} className="flex gap-3 text-sm leading-relaxed text-slate-700">
                      <Check size={16} className="mt-0.5 shrink-0 text-golddeep" /> {p}
                    </li>
                  ))}
                </ul>
              </div>
            </FadeUp>
            <FadeUp delay={0.2}>
              <p className="mt-8 flex items-start gap-3 text-sm text-slate-600">
                <CalendarClock size={18} className="mt-0.5 shrink-0 text-golddeep" />
                <span>
                  Pick a time on the calendar. If nothing fits, email Roy directly at{" "}
                  <a
                    data-testid="book-email-link"
                    href={`mailto:${CONTACT_EMAIL}`}
                    className="text-golddeep underline-offset-4 hover:underline"
                  >
                    {CONTACT_EMAIL}
                  </a>
                </span>
              </p>
            </FadeUp>
          </div>

          <FadeUp delay={0.1}>
            <div
              data-testid="calendly-embed-container"
              className="relative border border-gold/40 bg-white"
            >
              <div className="border-b border-gold/25 px-6 py-4">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-golddeep">
                  Live calendar · CollabIgnite
                </p>
              </div>
              <iframe
                data-testid="calendly-iframe"
                title="Book a conversation with Roy Buncome"
                src={CALENDLY_URL}
                className="h-[640px] w-full"
                frameBorder="0"
              />
            </div>
          </FadeUp>
        </div>
      </section>

      <section data-testid="book-faq" className="border-t border-gold/25 bg-cream">
        <div className="mx-auto max-w-4xl px-6 py-24 lg:px-10">
          <FadeUp>
            <SectionTag>Before You Book</SectionTag>
            <h2 className="mt-4 font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
              Straight answers
            </h2>
          </FadeUp>
          <div className="mt-12 space-y-4">
            {FAQ.map((f, i) => (
              <FadeUp key={f.q} delay={i * 0.05}>
                <FaqItem item={f} index={i} />
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="book-contact-alt" className="mx-auto max-w-4xl px-6 py-24 lg:px-10">
        <FadeUp>
          <SectionTag>Prefer Writing First?</SectionTag>
          <h2 className="mt-4 font-serif text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
            Send the situation ahead of time
          </h2>
          <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-slate-600">
            Your message lands directly in Roy's inbox. Context in advance makes your
            20 minutes sharper.
          </p>
          <div className="mt-10">
            <ContactForm />
          </div>
        </FadeUp>
      </section>
    </main>
  );
}
